import AddBoxIcon from '@mui/icons-material/AddBox';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Input from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useSnackbar } from 'notistack';
import React, { ChangeEvent, useCallback, useEffect, useState, type JSX } from 'react';
import { api } from '../../../apigateway';
import { Bearbeiterauswahl } from '../../../components/Bearbeiterauswahl/Bearbeiterauswahl';
import { ERROR_MESSAGE, WARNING_MESSAGE } from '../../../components/common/Alert';
import { CustomDateTimePicker } from '../../../components/common/customDatePickers';
import SearchPersonUnternehmen from '../../../components/common/SearchPersonUnternehmen';
import { SPACING_BETWEEN_FORM_FIELDS } from '../../../components/common/spacings';
import WarningSign from '../../../components/common/WarningSign';
import { Modal } from '../../../components/Modal';
import { PersonEingabeMaske } from '../../../components/PersonEingabeMaske/PersonEingabeMaske';
import { YesNoFormControl } from '../../../components/YesNoFormControl';
import { aktionErlaubt } from '../../../domain/aktionErlaubt';
import { aktualisierePerson } from '../../../domain/aktualisierePerson';
import { isAenderbar } from '../../../domain/isAenderbar';
import { AKTION_KUNDENDATEN_ERFASSEN, getDefaultPersonMitTyp } from '../../../frontendConstants';
import { useUser } from '../../../hooks/useUser';
import { PersonenTyp } from '../../../shared/constants';
import { getMessageFromError } from '../../../shared/throw';
import { benutzerprofil } from '../../../shared/url';
import { Besichtigung, Person, Vorgang, deprecated_Benutzer } from '../../../types';

const BESICHTIGUNGSORT_SACHVERSTAENDIGENBUERO = 'Sachverständigenbüro';

type Props = {
  readonly vorgang: Vorgang;
  readonly isLoading: boolean;
  readonly setLoading: (value: boolean) => void;
  readonly aktualisiereBesichtigungsdaten: (besichtigung: Partial<Besichtigung>) => void;
  readonly besichtigung: Partial<Besichtigung>;
  readonly onClose: () => void;
  readonly title: string;
};

export function Besichtigungdialog({ vorgang, isLoading, setLoading, aktualisiereBesichtigungsdaten, besichtigung, onClose, title }: Props): JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const { gruppenVonMandant } = useUser();
  const [aktuellerVermittler, setAktuellerVermittler] = useState<Person>(besichtigung?.vermittler ?? getDefaultPersonMitTyp(PersonenTyp.VERMITTLER));
  const [dialog, setDialog] = useState({ anlegen: false, aendern: false });
  const [besichtigungsdatum, setBesichtigungsdatum] = useState<Partial<Besichtigung>>({
    ...besichtigung,
    besichtigungstermin: besichtigung.besichtigungstermin ?? new Date().toISOString(),
    besichtigtVonUserId: besichtigung.besichtigtVonUserId ?? vorgang.userId
  });
  const [besichtiger, setBesichtiger] = useState<deprecated_Benutzer | null>(null);
  const [achtungModal, setAchtungModal] = useState(false);

  useEffect(() => {
    setAktuellerVermittler(besichtigungsdatum.vermittler ?? getDefaultPersonMitTyp(PersonenTyp.VERMITTLER));
  }, [besichtigungsdatum]);

  const uebernehmeAuftraggeberAdresse = useCallback(() => {
    if (vorgang?.auftraggeber) {
      const auftraggeber = vorgang.auftraggeber;
      setBesichtigungsdatum((prev) => ({
        ...prev,
        strasse: auftraggeber.strasse,
        ort: auftraggeber.ort,
        plz: auftraggeber.plz,
        besichtigungsortTyp: 'Auftraggeber'
      }));
    }
  }, [vorgang.auftraggeber]);

  const uebernehmeSachverstaendigenAdresse = useCallback(
    async (userId: string | null | undefined) => {
      if (!userId) {
        enqueueSnackbar('Adresse des Sachverständigen konnte nicht geladen werden, da kein Sachverständiger ausgewählt ist.', WARNING_MESSAGE);
        return;
      }

      try {
        const adresse = await api.request(benutzerprofil.getAdresse(userId));
        setBesichtigungsdatum((prev) => ({
          ...prev,
          strasse: adresse.strasse ?? prev.strasse,
          plz: adresse.plz ?? prev.plz,
          ort: adresse.ort ?? prev.ort
        }));
      } catch (error) {
        enqueueSnackbar(`Adresse des Sachverständigen konnte nicht geladen werden: ${getMessageFromError(error)}`, WARNING_MESSAGE);
      }
    },
    [enqueueSnackbar]
  );

  const handleVermittlerAnlegenDialog = () => {
    setAktuellerVermittler(getDefaultPersonMitTyp(PersonenTyp.VERMITTLER));
    setDialog((current) => ({ ...current, anlegen: true }));
  };

  const handleVermittlerLoeschen = () => {
    setBesichtigungsdatum((prev) => ({ ...prev, vermittlerId: null, vermittler: null }));
  };

  const handleVermittlerAendern = useCallback(
    (person: Person) => {
      setLoading(true);

      aktualisierePerson({
        ...person,
        mandant: vorgang.mandant
      })
        .then((neuePerson) => {
          setBesichtigungsdatum((prev) => ({ ...prev, vermittlerId: neuePerson.id, vermittler: person }));
          setDialog({ anlegen: false, aendern: false });
        })
        .catch((error) => enqueueSnackbar(getMessageFromError(error) ?? 'Vermittler konnte nicht gespeichert werden', ERROR_MESSAGE))
        .finally(() => setLoading(false));
    },
    [enqueueSnackbar, setLoading, vorgang.mandant]
  );

  const handleNeuenBesichtiger = useCallback(
    (benutzer: deprecated_Benutzer) => {
      setBesichtiger(benutzer);
      if (benutzer.id !== besichtigungsdatum.besichtigtVonUserId) {
        setAchtungModal(true);
      } else {
        setBesichtigungsdatum((prev) => ({ ...prev, besichtigtVonUserId: benutzer.id }));
        if (besichtigungsdatum.besichtigungsortTyp === BESICHTIGUNGSORT_SACHVERSTAENDIGENBUERO) {
          void uebernehmeSachverstaendigenAdresse(benutzer.id);
        }
      }
    },
    [besichtigungsdatum.besichtigtVonUserId, besichtigungsdatum.besichtigungsortTyp, uebernehmeSachverstaendigenAdresse]
  );

  const isDisabled =
    !vorgang || isLoading || !isAenderbar(vorgang) || !aktionErlaubt(AKTION_KUNDENDATEN_ERFASSEN, gruppenVonMandant(vorgang?.mandant || ''), vorgang?.status);

  return (
    <>
      <Modal
        sx={{ maxWidth: '600px' }}
        openModal={true}
        setOpenModal={onClose}
        title={title}
        body={
          <>
            <Grid container spacing={SPACING_BETWEEN_FORM_FIELDS}>
              <Grid size={12}>
                <TextField
                  variant="standard"
                  select
                  label="Besichtigungsort"
                  value={besichtigungsdatum.besichtigungsortTyp ?? ''}
                  disabled={isDisabled}
                  onChange={(event) => {
                    const neuerTyp = event.target.value;
                    setBesichtigungsdatum({ ...besichtigungsdatum, besichtigungsortTyp: neuerTyp });
                    if (neuerTyp === BESICHTIGUNGSORT_SACHVERSTAENDIGENBUERO) {
                      void uebernehmeSachverstaendigenAdresse(besichtigungsdatum.besichtigtVonUserId);
                    }
                  }}
                  fullWidth
                  data-testid="besichtigungsdaten-besichtigungsorttyp"
                >
                  <MenuItem value="">Bitte auswählen</MenuItem>
                  <MenuItem value="Reparaturfirma">Reparaturfirma</MenuItem>
                  <MenuItem value="Werkstatt">Werkstatt</MenuItem>
                  <MenuItem value="Auftraggeber">Auftraggeber</MenuItem>
                  <MenuItem value="Sachverständigenbüro">Sachverständigenbüro</MenuItem>
                  <MenuItem value="Arbeitsplatz">Arbeitsplatz</MenuItem>
                  <MenuItem value="Abschlepphof">Abschlepphof</MenuItem>
                  <MenuItem value="Sonstiges">Sonstiges</MenuItem>
                </TextField>
              </Grid>
              <Grid size={12}>
                <TextField
                  variant="standard"
                  label="Straße und Hausnummer"
                  value={besichtigungsdatum.strasse ?? ''}
                  disabled={isDisabled}
                  onChange={(event) => {
                    setBesichtigungsdatum({ ...besichtigungsdatum, strasse: event.target.value });
                  }}
                  fullWidth
                  data-testid="besichtigungsdaten-strasse"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  variant="standard"
                  label="PLZ"
                  value={besichtigungsdatum.plz ?? ''}
                  disabled={isDisabled}
                  onChange={(event) => setBesichtigungsdatum({ ...besichtigungsdatum, plz: event.target.value })}
                  fullWidth
                  data-testid="besichtigungsdaten-plz"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 8 }}>
                <TextField
                  variant="standard"
                  label="Ort"
                  value={besichtigungsdatum.ort ?? ''}
                  disabled={isDisabled}
                  onChange={(event) => {
                    setBesichtigungsdatum({ ...besichtigungsdatum, ort: event.target.value });
                  }}
                  fullWidth
                  data-testid="besichtigungsdaten-ort"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 12 }}>
                <Button color="primary" variant="contained" onClick={uebernehmeAuftraggeberAdresse} disabled={!vorgang?.auftraggeberId}>
                  Auftraggeberadresse übernehmen
                </Button>
              </Grid>
              <Grid size={12}>
                <SearchPersonUnternehmen
                  mandantId={vorgang?.mandant}
                  isDisabled={isDisabled}
                  label="Vermittler suchen"
                  onSelect={(person) => setBesichtigungsdatum({ ...besichtigungsdatum, vermittlerId: person.id, vermittler: person as Person })}
                  testPraefix="vermittler"
                  personenUnternehmenTyp={PersonenTyp.VERMITTLER}
                />
              </Grid>
              <Grid size={12}>
                <WarningSign
                  show={Boolean(aktuellerVermittler === getDefaultPersonMitTyp(PersonenTyp.VERMITTLER) && besichtigungsdatum.vermittlerId)}
                  text="Der Vermittler wurde gelöscht oder Sie haben nicht die erforderlichen Rechte, diesen zu sehen."
                />
                <FormControl variant="standard" disabled={true} fullWidth>
                  <InputLabel htmlFor="standard-adornment-password">zugeordneter Vermittler</InputLabel>
                  <Input
                    data-testid="besichtigungsdaten-vermittler"
                    value={aktuellerVermittler ? `${aktuellerVermittler?.vorname ?? ''} ${aktuellerVermittler?.nachname ?? ''}` : ''}
                    onChange={() => undefined}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          disabled={!besichtigungsdatum.vermittlerId || isDisabled}
                          onClick={() => {
                            setDialog((current) => ({ ...current, aendern: true }));
                          }}
                          data-testid="vermittler-bearbeiten"
                          size="large"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            handleVermittlerAnlegenDialog();
                          }}
                          disabled={isDisabled}
                          data-testid="vermittler-anlegen"
                          size="large"
                        >
                          <AddBoxIcon />
                        </IconButton>
                        <IconButton
                          disabled={!besichtigungsdatum.vermittlerId || isDisabled}
                          onClick={() => {
                            handleVermittlerLoeschen();
                          }}
                          data-testid="vermittler-loeschen"
                          size="large"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <CustomDateTimePicker
                  fullWidth
                  disabled={isDisabled}
                  value={besichtigungsdatum.besichtigungstermin ?? new Date().toISOString()}
                  onChange={(date: Date | null) => {
                    setBesichtigungsdatum({ ...besichtigungsdatum, besichtigungstermin: date ? date.toISOString() : null });
                  }}
                  label="Besichtigungstermin"
                  data-testid="besichtigungsdaten-besichtigungstermin"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Bearbeiterauswahl
                  label="Besichtigung durchgeführt von"
                  mandant={vorgang.mandant ?? ''}
                  userId={besichtigungsdatum.besichtigtVonUserId}
                  gruppen={['Sachverstaendiger']}
                  disabled={isDisabled}
                  onChange={handleNeuenBesichtiger}
                  dataTestId="besichtigt-von-eingabe"
                />
              </Grid>

              <Grid size={12}>
                <YesNoFormControl
                  labelText="Fahrzeug in demontiertem Zustand"
                  value={besichtigungsdatum.demontierterZustand}
                  disabled={isDisabled}
                  onChange={(jaNein) => {
                    setBesichtigungsdatum({ ...besichtigungsdatum, demontierterZustand: jaNein });
                  }}
                  dataTestid="besichtigungsdaten-demontiert"
                  fullWidth
                />
              </Grid>
              <Grid size={12}>
                <YesNoFormControl
                  labelText="Hebebühne"
                  value={besichtigungsdatum.hebebuehne}
                  disabled={isDisabled}
                  onChange={(jaNein) => {
                    setBesichtigungsdatum({ ...besichtigungsdatum, hebebuehne: jaNein });
                  }}
                  dataTestid="besichtigungsdaten-hebebuehne"
                  fullWidth
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  variant="standard"
                  select
                  label="Zerlegungszustand"
                  value={besichtigungsdatum.zerlegungszustand ?? ''}
                  disabled={isDisabled}
                  onChange={(event) => {
                    setBesichtigungsdatum({ ...besichtigungsdatum, zerlegungszustand: event.target.value });
                  }}
                  fullWidth
                  data-testid="besichtigungsdaten-zerlegungszustand"
                >
                  <MenuItem value="">Bitte auswählen</MenuItem>
                  <MenuItem value="unzerlegt">unzerlegt</MenuItem>
                  <MenuItem value="teilzerlegt">teilzerlegt</MenuItem>
                  <MenuItem value="zerlegt">zerlegt</MenuItem>
                  <MenuItem value="Schadenbereich freigelegt">Schadenbereich freigelegt</MenuItem>
                  <MenuItem value="teilrepariert">teilrepariert</MenuItem>
                  <MenuItem value="repariert">repariert</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 12 }}>
                <Grid size={12} display="flex" alignItems="center">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={besichtigungsdatum?.ohneBesichtigung ?? false}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          setBesichtigungsdatum({ ...besichtigungsdatum, ohneBesichtigung: event.target.checked });
                        }}
                        name="ohneBesichtigung"
                        color="primary"
                        data-testid="ohneBesichtigungCheckbox"
                      />
                    }
                    label="Ohne Besichtigung"
                  />
                </Grid>
              </Grid>
            </Grid>

            {(dialog.anlegen || dialog.aendern) && (
              <PersonEingabeMaske
                personDatensatz={dialog.aendern ? (besichtigungsdatum?.vermittler ?? null) : null}
                typ={PersonenTyp.VERMITTLER}
                onClose={() => {
                  setDialog({ anlegen: false, aendern: false });
                }}
                aktualisierePerson={handleVermittlerAendern}
                isDisabled={isDisabled}
                mandant={vorgang.mandant}
                testPraefix="vermittler"
              />
            )}
          </>
        }
        footer={
          <>
            <Grid>
              <Button color="secondary" variant="contained" onClick={() => onClose()} data-testid="besichtigungsdaten-abbrechen">
                Abbrechen
              </Button>
            </Grid>
            <Grid>
              <Button
                color="primary"
                variant="contained"
                onClick={() => {
                  aktualisiereBesichtigungsdaten(besichtigungsdatum);
                }}
                data-testid="besichtigungsdaten-speichern"
              >
                Übernehmen
              </Button>
            </Grid>
          </>
        }
      ></Modal>
      <Modal
        sx={{ maxWidth: '400px' }}
        openModal={achtungModal}
        title="Achtung"
        body={
          <>
            Bitte bestätigen Sie, dass die Besichtigung von{' '}
            <strong>
              {besichtiger?.given_name} {besichtiger?.family_name}
            </strong>{' '}
            durchgeführt wurde
          </>
        }
        footer={
          <>
            <Grid>
              <Button
                onClick={() => {
                  setAchtungModal(false);
                  setBesichtiger(null);
                }}
                variant="contained"
                color="secondary"
              >
                Abbrechen
              </Button>
            </Grid>
            <Grid>
              <Button
                onClick={() => {
                  setAchtungModal(false);
                  setBesichtigungsdatum((prev) => ({ ...prev, besichtigtVonUserId: besichtiger?.id }));
                  if (besichtigungsdatum.besichtigungsortTyp === BESICHTIGUNGSORT_SACHVERSTAENDIGENBUERO) {
                    void uebernehmeSachverstaendigenAdresse(besichtiger?.id);
                  }
                  setBesichtiger(null);
                }}
                variant="contained"
                color="primary"
              >
                Bestätigen
              </Button>
            </Grid>
          </>
        }
      ></Modal>
    </>
  );
}
